import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Shield, Clock, Award, Star, CheckCircle, ArrowRight, ExternalLink, Zap } from 'lucide-react';
import TrustedReviews from './TrustedReviews';
import { ResponsiveImage } from './common/ResponsiveImage';
// The `?as=picture` directive emits AVIF + WebP + JPEG at each requested
// width and returns `{ sources, img: { src, w, h } }`. Widths match the
// presets in @/lib/images so every call site stays consistent. Must stay in
// sync with the `HERO_SIZES` constant in vite.config.ts's preloadHeroImage
// plugin so the browser preloads the exact variant we render.
import heroPicture from '@/assets/hero-bg.jpg?w=640;1024;1440;1920&format=avif;webp;jpg&as=picture';

const Hero = () => {
  return (
    <section className="relative bg-navy-dark min-h-[600px] lg:min-h-[680px] flex items-center overflow-hidden">
      {/* Background image with overlay */}
      <div className="absolute inset-0">
        <ResponsiveImage
          picture={heroPicture}
          sizes="100vw"
          alt="Licensed electrician performing electrical panel work in a residential home"
          className="w-full h-full object-cover"
          priority
        />
        {/* Multi-layer gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy/90 to-navy/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/60 via-transparent to-navy-dark/30" />
      </div>

      {/* Subtle animated accent glow */}
      <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-orange/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-orange/5 rounded-full blur-2xl pointer-events-none" />

      <div className="container-custom relative z-10 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">

          {/* LEFT COLUMN – Content + CTAs */}
          <div className="lg:col-span-7 animate-fade-in">
            {/* License badge */}
            <div className="inline-flex items-center gap-2 bg-orange/15 border border-orange/30 rounded-full px-4 py-1.5 mb-5">
              <Shield className="w-4 h-4 text-orange" />
              <span className="text-orange font-semibold text-xs tracking-wide uppercase">
                FL License EC13011028 • Licensed & Insured
              </span>
            </div>

            {/* H1 */}
            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl xl:text-[3.25rem] font-extrabold leading-tight mb-3">
              Licensed Electrician Serving{' '}
              <span className="text-orange">Volusia, Seminole &amp; Central&nbsp;Florida</span>
            </h1>

            {/* Supporting H2 */}
            <h2 className="text-white/90 text-2xl md:text-3xl lg:text-4xl font-bold mb-5">
              Residential &amp; Commercial Electrical
            </h2>

            {/* Subheadline */}
            <p className="text-white/85 text-base md:text-lg lg:text-xl leading-relaxed mb-7 max-w-2xl">
              Your local electrician near Deltona, Sanford, Daytona Beach, New Smyrna Beach,
              Lake Mary, DeLand and Ormond Beach. Expert panel upgrades, EV chargers,
              generators, and full electrical services. Family-owned, licensed EC13011028,
              with <strong className="text-white">470+ happy customers</strong> and same-day availability.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-7">
              <a
                href="tel:4079707211"
                className="group relative bg-orange hover:bg-orange-dark text-navy font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center text-lg shadow-lg shadow-orange/25 hover:shadow-orange/40 hover:scale-[1.02]"
              >
                <Phone size={22} className="mr-3 group-hover:animate-pulse" />
                Call Now: (407) 970-7211
              </a>
              <Link
                to="/contact"
                className="group bg-white/10 hover:bg-white/20 backdrop-blur-sm border-2 border-white/30 hover:border-white/50 text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center text-lg"
              >
                Get Free Quote
                <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Trust chips */}
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { icon: Shield, text: 'Licensed & Insured' },
                { icon: Clock, text: 'Same-Day Service' },
                { icon: Award, text: 'Family Owned' },
                { icon: Star, text: '5.0 ★ Rated' },
                { icon: Zap, text: 'Emergency 24/7' },
              ].map(({ icon: Icon, text }) => (
                <div
                  key={text}
                  className="inline-flex items-center gap-1.5 bg-white/8 border border-white/10 rounded-full px-3 py-1.5 text-white/80 text-xs font-medium"
                >
                  <Icon className="w-3.5 h-3.5 text-orange flex-shrink-0" />
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT COLUMN – Trust panel */}
          <div className="lg:col-span-5 animate-fade-in" style={{ animationDelay: '0.15s' }}>
            <div className="relative">
              {/* Glow backdrop */}
              <div className="absolute -inset-3 bg-gradient-to-br from-orange/10 to-navy-light/20 rounded-2xl blur-xl pointer-events-none" />

              <div className="relative bg-white/[0.07] backdrop-blur-md border border-white/15 rounded-2xl p-5 md:p-6 space-y-5">
                {/* Header */}
                <div className="text-center pb-3 border-b border-white/10">
                  <p className="text-orange font-bold text-sm uppercase tracking-wider">Verified Reviews</p>
                  <p className="text-white/60 text-xs mt-1">Real ratings from real customers</p>
                </div>

                {/* Review cards (reuses TrustedReviews logic) */}
                <TrustedReviews />

                {/* Quick stats row */}
                <div className="grid grid-cols-3 gap-3 pt-2">
                  {[
                    { value: '470+', label: 'Customers' },
                    { value: '5.0', label: 'Avg Rating' },
                    { value: '4+', label: 'Years Local' },
                  ].map(({ value, label }) => (
                    <div key={label} className="text-center">
                      <div className="text-white font-extrabold text-xl lg:text-2xl">{value}</div>
                      <div className="text-white/80 text-[10px] uppercase tracking-wider font-medium">{label}</div>
                    </div>
                  ))}
                </div>

                {/* Service highlights */}
                <div className="space-y-2.5 pt-2 border-t border-white/10">
                  {[
                    'EV Charger Installation (Tesla & All Brands)',
                    'Panel Upgrades (100A–400A)',
                    'Generac & Kohler Generator Installs',
                    'Residential & Commercial Wiring',
                  ].map((service) => (
                    <div key={service} className="flex items-center gap-2.5">
                      <CheckCircle className="w-4 h-4 text-orange flex-shrink-0" />
                      <span className="text-white/80 text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
